import type { ReloadOptions } from "@inertiajs/core";
import {
  Head,
  Link,
  router,
  useForm,
  usePage,
  usePoll,
} from "@inertiajs/react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  CheckCircle,
  ClipboardList,
  Clock,
  Copy,
  DollarSign,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Flag,
  Info,
  LoaderCircle,
  Mail,
  Package,
  Plus,
  Trash2,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { type ComponentProps, useEffect, useState } from "react";
import Dropzone from "shadcn-dropzone";

import PurchaseRequestApprovalPanel from "@/components/approvals/purchase-request-approval-panel";
import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import CommentsThread from "@/components/comments-thread";
import CurrencyFormatHint from "@/components/currency-format-hint";
import DeliveryNotesTable from "@/components/delivery-notes-table";
import GroupGate from "@/components/group-gate";
import { HelpButton, HelpDialog } from "@/components/help-dialog";
import InlineEditableCombobox from "@/components/inline-editable-combobox";
import InlineEditableField from "@/components/inline-editable-field";
import InvitationsManager from "@/components/invitations-manager";
import InvoicesTable from "@/components/invoices-table";
import PdfViewerSheet from "@/components/pdf-viewer-sheet";
import PriceComparisonTable from "@/components/price-comparison-table";
import PurchaseHistorySheet from "@/components/purchase-history-sheet";
import PurchaseRequestAssignmentsManager from "@/components/purchase-request-assignments-manager";
import PurchaseRequestItemsTable, {
  useRequestItemUpdates,
} from "@/components/purchase-request-items-table";
import PurchaseSelectionTable from "@/components/purchase-selection-table";
import QuoteHistoryDialog from "@/components/quote-history-dialog";
import StatusBadge from "@/components/request-status/status-badge";
import StatusHistoryDialog from "@/components/request-status/status-history-dialog";
import StatusPicker from "@/components/request-status/status-picker";
import SelectedItemsTable from "@/components/selected-items-table";
import { StalenessAlert } from "@/components/staleness-alert";
import TutorialBadge from "@/components/tutorial-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
} from "@/components/ui/button-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { GROUPS } from "@/constants/groups";
import {
  canExportPurchaseOrders,
  purchaseRequestStatusColor,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { formatCurrency } from "@/helpers/currency-formatter";
import { useCompleteExport } from "@/hooks/use-complete-export";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import { canSeeApprovalComponent } from "@/lib/authz";
import { cn } from "@/lib/utils";
import type {
  BreadcrumbItem,
  Comment,
  DeliveryNote,
  LastPurchaseSummary,
  PriceComparison,
  PurchaseHistoryEntry,
  PurchaseRequest,
  PurchaseRequestApprovalState,
  PurchaseRequestAssignments,
  PurchaseRequestInvoiceSummary,
  PurchaseRequestPermissions,
  Quote,
  QuoteDocument,
  QuoteInvitation,
  RequestItem,
  SharedData,
} from "@/types";
import { formatFileSize, isPdfUrl } from "@/utils/file";

interface PurchaseRequestShowProps {
  default_supplier_id?: number;
  purchase_request: PurchaseRequest & {
    general_observations?: string;
    sector: {
      id: number;
      name: string;
      code?: string;
    };
  };
  can_edit_status?: boolean;
  status_history?: {
    at: string;
    by?: { id: number; name?: string } | null;
    from?: string | null;
    to?: string | null;
  }[];
  comments: Comment[];
  quote_documents: QuoteDocument[];
  quotes: Quote[];
  request_items: RequestItem[];
  sectors?: { id: number; name: string }[];
  quote_invitations: QuoteInvitation[];
  price_comparison?: PriceComparison | null;
  // New: last purchase summary per request item
  last_purchases_by_request_item_id?: Record<number, LastPurchaseSummary>;
  // New (deferred): full history per request item (loaded on demand)
  purchase_history_by_request_item_id?: Record<number, PurchaseHistoryEntry[]>;
  assignments: PurchaseRequestAssignments;
  approval: PurchaseRequestApprovalState;
  delivery_notes?: DeliveryNote[];
  invoices?: PurchaseRequestInvoiceSummary[];
  can?: PurchaseRequestPermissions;
}

// Status colors and labels come from shared constants

const docStatusConfig = {
  pending: {
    label: "En espera",
    color: "bg-yellow-100 text-yellow-800",
    icon: "🟡",
  },
  processing: {
    label: "Procesando...",
    color: "bg-blue-100 text-blue-800",
    icon: "🔵",
  },
  processed: {
    label: "Procesado",
    color: "bg-green-100 text-green-800",
    icon: "✅",
  },
  failed: { label: "Error", color: "bg-red-100 text-red-800", icon: "❌" },
};

// Dates come already formatted from backend as DD/MM/YYYY

export default function PurchaseRequestShow({
  default_supplier_id,
  purchase_request,
  can_edit_status = false,
  status_history = [],
  comments,
  quote_documents,
  quotes,
  request_items,
  sectors = [],
  quote_invitations = [],
  price_comparison = null,
  last_purchases_by_request_item_id = {},
  assignments,
  approval,
  delivery_notes,
  invoices,
  can,
}: PurchaseRequestShowProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [qtyStr, setQtyStr] = useState("");

  const routes = useOrganizationRoutes();
  const [selectedProduct, setSelectedProduct] = useState<AsyncOption | null>(
    null,
  );
  // Product picker for editing existing items (async)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerItem, setProductPickerItem] =
    useState<RequestItem | null>(null);
  const [productPickerOption, setProductPickerOption] =
    useState<AsyncOption | null>(null);
  // Purchase history sheet state
  const [historySheetOpen, setHistorySheetOpen] = useState(false);
  const [historyItemId, setHistoryItemId] = useState<number | null>(null);
  const [historyItemDescription, setHistoryItemDescription] = useState<
    string | null
  >(null);

  // Help dialogs
  const overviewHelp = useHelpDialog("purchaseRequestOverview");
  const generalInfoHelp = useHelpDialog("generalInfo");
  const requestItemsHelp = useHelpDialog("requestItems");
  const aiQuotesHelp = useHelpDialog("aiQuotes");
  const quoteManagementHelp = useHelpDialog("quoteManagement");
  const comparisonHelp = useHelpDialog("comparisonAndSelection");

  const openHistory = (requestItemId: number, description: string) => {
    setHistoryItemId(requestItemId);
    setHistoryItemDescription(description);
    setHistorySheetOpen(true);
  };
  // Tabs manage view between comparativa and selección

  // Group quotes by supplier and keep only the latest per supplier
  const groupsBySupplier = quotes.reduce((acc: Record<string, Quote[]>, q) => {
    const isDefault = default_supplier_id
      ? q.supplier.id === default_supplier_id
      : q.supplier.name === "Proveedor por asignar";
    const key = isDefault ? `unknown-${q.id}` : `supplier-${q.supplier.id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(q);
    return acc;
  }, {});

  const supplierQuoteGroups = Object.values(groupsBySupplier).map((arr) => {
    const sorted = arr.slice().sort((a, b) => {
      const ak = a.created_at ? new Date(a.created_at).getTime() : a.id;
      const bk = b.created_at ? new Date(b.created_at).getTime() : b.id;
      return bk - ak; // DESC
    });
    return { latest: sorted[0], history: sorted.slice(1) };
  });

  const latestQuotesOnly = supplierQuoteGroups.map((g) => g.latest);

  // Check default supplier only on latest per supplier
  const quotesWithDefaultSupplier = latestQuotesOnly.filter((quote) =>
    default_supplier_id
      ? quote.supplier.id === default_supplier_id
      : quote.supplier.name === "Proveedor por asignar",
  );
  const hasDefaultSupplierQuotes = quotesWithDefaultSupplier.length > 0;

  // We no longer receive full product catalog or suppliers here.
  // Product selection is handled via async modal picker.

  // Units shared from backend via Inertia
  const shared = usePage<SharedData>().props;
  const userGroups = shared.auth?.groups ?? [];

  const purchaseRequestsHref = routes.purchaseRequests.index();
  const purchaseRequestHref = routes.purchaseRequests.show(purchase_request.id);
  const requestItemsHref = routes.requestItems.index();
  const requestItemHref = (item: { id: number }) =>
    routes.requestItems.show(item.id);
  const quoteDocumentHref = (docId: number) =>
    routes.quoteDocuments.show(docId);
  const uploadAndProcessQuotesHref =
    routes.purchaseRequests.uploadAndProcessQuotes(purchase_request.id);
  const requestCompleteExportHref =
    routes.purchaseRequests.requestCompleteExport(purchase_request.id);
  const completeExportStatusHref =
    routes.purchaseRequests.completeExportStatus(purchase_request.id);
  const newQuoteRequestHref = routes.purchaseRequests.quotes.newQuoteRequest(
    purchase_request.id,
  );
  const newQuoteHref = routes.purchaseRequests.quotes.new(purchase_request.id);
  const purchaseRequestQuoteHref = (quoteId: number) =>
    routes.purchaseRequests.quotes.show(purchase_request.id, quoteId);
  const commentsHref = routes.purchaseRequests.comments(purchase_request.id);
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Solicitudes Compras",
      href: purchaseRequestsHref,
    },
  ];
  const canEditAssignments =
    userGroups.includes(GROUPS.buyer) ||
    userGroups.includes(GROUPS.supervisor) ||
    userGroups.includes(GROUPS.admin) ||
    userGroups.includes(GROUPS.owner);
  const unitOptions = shared.enums?.units ?? [
    { label: "UN - Unidades", value: "UN" },
  ];
  const canDownloadComplete = can?.downloadComplete ?? false;
  const {
    requestExport: requestCompleteExport,
    isRequesting: requestingCompleteExport,
    status: exportStatus,
    error: exportError,
  } = useCompleteExport({
    requestHref: requestCompleteExportHref,
    statusHref: completeExportStatusHref,
  });

  // Determine if the origin document is a PDF or image for proper handling
  const originIsPdf = purchase_request?.pdf?.url
    ? isPdfUrl(purchase_request.pdf.url)
    : false;

  // Visual-only stats for header chips (no core logic touched)
  const totalItems = request_items.length;
  const assignedCount = request_items.filter(
    (i: RequestItem) => i.product_id != null,
  ).length;
  const unassignedCount = Math.max(totalItems - assignedCount, 0);
  const pricedCount = request_items.filter(
    (i: RequestItem) => Number(i.average_price ?? 0) > 0,
  ).length;
  const estimatedTotalARS = request_items.reduce(
    (sum: number, i: RequestItem) => {
      const avg = Number(i.average_price ?? 0);
      const qty = Number(i.quantity ?? 0);
      return sum + avg * qty;
    },
    0,
  );
  const hasUnmatchedItems = unassignedCount > 0;

  const openProductPicker = (item: RequestItem) => {
    setProductPickerItem(item);
    const current =
      item.product_id && item.product
        ? { value: item.product_id, label: item.product.normalized_name }
        : null;
    setProductPickerOption(current);
    setProductPickerOpen(true);
  };

  // Check if any items have raw descriptions
  const hasRawDescriptions = request_items.some(
    (i: RequestItem) =>
      typeof i.raw_description === "string" && i.raw_description.trim() !== "",
  );

  // Use the hook for handling item updates
  const { handleUpdateItem, handleDeleteItem } =
    useRequestItemUpdates(requestItemHref);

  const { data, setData, post, processing } = useForm({
    pdfs: [] as File[],
  });

  // Form for adding new RequestItem
  const {
    data: newItemData,
    setData: setNewItemData,
    post: postNewItem,
    processing: creatingItem,
    reset: resetNewItem,
  } = useForm({
    request_item: {
      purchase_request_id: purchase_request.id,
      product_id: null as number | null,
      quantity: 1 as number,
      unit: "" as string,
    },
  });

  const parseCommaNumber = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    if (!/^[0-9]+(,[0-9]+)?$/.test(raw)) return null;
    const parsed = Number(raw.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Metrics for "Cotizaciones Generadas" (visual only)
  const docCounts = quote_documents.reduce(
    (acc: Record<string, number>, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      return acc;
    },
    { pending: 0, processing: 0, processed: 0, failed: 0 } as Record<
      string,
      number
    >,
  );
  // Previously computed metrics not used in UI, removed to satisfy lint rules.

  // Poll every 30 seconds for processing updates, but pause while history sheet is open.
  type PollReloadOptions = ReloadOptions & { preserveState?: boolean };
  const pollReloadOptions: PollReloadOptions = {
    // Limit reload to props that reflect processing changes
    only: ["quote_documents", "quotes", "price_comparison", "approval"],
    preserveState: true,
  };

  const { start: startPoll, stop: stopPoll } = usePoll(30000, pollReloadOptions);

  useEffect(() => {
    if (historySheetOpen) {
      stopPoll();
    } else {
      startPoll();
    }
  }, [historySheetOpen, startPoll, stopPoll]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    setData("pdfs", [...data.pdfs, ...acceptedFiles]);
  };

  const handleFileRemove = (index: number) => {
    const newFiles = data.pdfs.filter((_, i) => i !== index);
    setData("pdfs", newFiles);
  };

  const handleUploadSubmit = () => {
    post(uploadAndProcessQuotesHref, {
      forceFormData: true,
      onSuccess: () => {
        setUploadDialogOpen(false);
        setData("pdfs", []);
      },
    });
  };

  const handleDeleteDocument = (docId: number) => {
    if (
      window.confirm(
        "¿Estás seguro? Eliminar este documento removerá permanentemente el archivo y cualquier cotización generada. Esta acción no se puede deshacer.",
      )
    ) {
      router.delete(quoteDocumentHref(docId));
    }
  };

  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: `Solicitud #${purchase_request.id}`,
      href: "#",
    },
  ];

  // Role presets for readability and future reuse
  const CAN_MANAGE_QUOTES = [GROUPS.buyer, GROUPS.supervisor, GROUPS.admin, GROUPS.owner];

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Solicitud #${purchase_request.id}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={purchaseRequestsHref}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Solicitudes de compra
              </Link>
            </Button>
            <div className="flex flex-col">
              <h1 className="text-3xl font-bold">
                Solicitud #{purchase_request.id}
              </h1>
              {purchase_request.legacy_request_number && (
                <span className="text-sm text-muted-foreground font-medium">
                  Nº Original: {purchase_request.legacy_request_number}
                </span>
              )}
            </div>
            {purchase_request?.trace_id && (
              <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="opacity-70">Trace ID:</span>
                <code className="rounded bg-muted px-1 py-0.5">
                  {purchase_request.trace_id}
                </code>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() =>
                        navigator.clipboard?.writeText?.(
                          purchase_request.trace_id ?? "",
                        )
                      }
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copiar Trace ID</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <ButtonGroup className="flex-wrap">
              <TutorialBadge asButton />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled
                className={`disabled:opacity-100 ${purchaseRequestStatusColor(purchase_request.status)}`}
                title={purchaseRequestStatusLabel(purchase_request.status)}
              >
                {purchaseRequestStatusLabel(purchase_request.status)}
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link href={`${purchaseRequestHref}/activity_log`}>
                  <ClipboardList className="h-4 w-4 mr-1" /> Ver historial
                </Link>
              </Button>
              {canDownloadComplete && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    requestingCompleteExport ||
                    exportStatus === "processing"
                  }
                  onClick={requestCompleteExport}
                >
                  {requestingCompleteExport || exportStatus === "processing" ? (
                    <LoaderCircle className="h-4 w-4 mr-1 animate-spin" />
                  ) : exportStatus === "failed" ? (
                    <TriangleAlert className="h-4 w-4 mr-1" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  {exportStatus === "processing"
                    ? "Generando..."
                    : exportStatus === "failed"
                      ? "Error - Reintentar"
                      : "Descargar SC Completa"}
                </Button>
              )}
              {exportError && (
                <span className="text-xs text-red-600">{exportError}</span>
              )}
              {can_edit_status && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setStatusDialogOpen(true)}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Cambiar estado
                </Button>
              )}
              <HelpButton
                onClick={overviewHelp.open}
                label="Ayuda"
                variant="outline"
              />
            </ButtonGroup>
            <StatusPicker
              open={statusDialogOpen}
              onOpenChange={setStatusDialogOpen}
              value={purchase_request.status}
              onSubmit={(newStatus) => {
                if (!newStatus || newStatus === purchase_request.status) return;
                router.patch(
                  purchaseRequestHref,
                  { purchase_request: { status: newStatus } },
                  { preserveScroll: true },
                );
              }}
            />
            {purchase_request.priority && (
              <Badge variant="destructive">
                <Flag className="h-3 w-3 mr-1" />
                Prioritario
              </Badge>
            )}
            {/* Botón de eliminar movido al final de la página */}
          </div>
        </div>

        <StalenessAlert
          status={purchase_request.staleness_status}
          daysSinceActivity={purchase_request.days_since_last_activity}
        />

        {/* General Information Card */}
        <Card>
          <CardHeader className="bg-gradient-to-br from-muted/40 to-transparent rounded-t-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Información General
                </CardTitle>
                <CardDescription>
                  Edita en línea con el ícono de lápiz. Enter guarda, Esc
                  cancela.
                </CardDescription>
              </div>
              <HelpButton
                onClick={generalInfoHelp.open}
                label="Ayuda"
                variant="outline"
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {purchase_request.deadline_at && (
                <Badge variant="outline" className="px-2 py-0.5">
                  <Calendar className="h-3 w-3 mr-1" />
                  {purchase_request.deadline_at}
                </Badge>
              )}
              {/* Ver fuente: texto crudo (cuando no hay documento origen) */}
              {purchase_request?.raw_input_text &&
              !purchase_request?.pdf?.url ? (
                <Dialog
                  open={sourceDialogOpen}
                  onOpenChange={setSourceDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button size="xs" variant="outline" className="h-6">
                      <FileText className="h-3 w-3 mr-1" /> Ver fuente (texto)
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Texto original de la solicitud</DialogTitle>
                      <DialogDescription>
                        Este es el texto que se usó para generar la solicitud de
                        compra.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3">
                      <pre className="whitespace-pre-wrap text-sm">
                        {purchase_request.raw_input_text}
                      </pre>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : null}
            </div>
            <div className="mt-3">
              <PurchaseRequestAssignmentsManager
                purchaseRequest={purchase_request}
                assignments={assignments}
                canEdit={canEditAssignments}
                leadingAction={
                  purchase_request?.pdf?.url ? (
                    originIsPdf ? (
                      <PdfViewerSheet
                        pdfUrl={purchase_request.pdf.url}
                        title={`Solicitud #${purchase_request.id}`}
                      >
                        <Button size="sm" variant="outline" className="gap-2">
                          <FileText className="h-4 w-4" /> Ver documento origen
                        </Button>
                      </PdfViewerSheet>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="gap-2"
                      >
                        <a
                          href={purchase_request.pdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FileText className="h-4 w-4" /> Ver documento origen
                        </a>
                      </Button>
                    )
                  ) : null
                }
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InlineEditableField
                  label="ID Solicitud"
                  value={String(purchase_request.id)}
                  field="id"
                  updateUrl={purchaseRequestHref}
                  icon={FileText}
                  disabled={true}
                />

                {purchase_request.legacy_request_number && (
                  <InlineEditableField
                    label="Nº Solicitud Original"
                    value={purchase_request.legacy_request_number}
                    field="legacy_request_number"
                    icon={FileText}
                    disabled={true}
                  />
                )}

                <InlineEditableField
                  label="Fecha de Solicitud"
                  value={purchase_request.request_date}
                  field="request_date"
                  type="date"
                  updateUrl={purchaseRequestHref}
                  icon={Calendar}
                  formatValue={(value) =>
                    value ? String(value) : "No especificada"
                  }
                />

                <InlineEditableField
                  label="Fecha límite"
                  value={purchase_request.deadline_at}
                  field="deadline_at"
                  type="datetime"
                  updateUrl={purchaseRequestHref}
                  icon={Calendar}
                  formatValue={(value) =>
                    value ? String(value) : "Sin fecha límite"
                  }
                  placeholder="DD/MM/AAAA HH:mm"
                />

                <InlineEditableCombobox
                  label="Sector (Obra)"
                  value={purchase_request.sector?.id}
                  field="sector_id"
                  updateUrl={purchaseRequestHref}
                  icon={Building2}
                  options={sectors.map((sector) => ({
                    label: sector.name,
                    value: sector.id,
                  }))}
                  formatValue={() =>
                    purchase_request.sector?.name || "No asignado"
                  }
                  searchPlaceholder="Buscar sector..."
                  emptyMessage="No se encontró ningún sector"
                />

                <InlineEditableField
                  label="Número de sector"
                  value={purchase_request.sector?.code}
                  field="sector_code"
                  icon={Building2}
                  disabled={true}
                  formatValue={(value) =>
                    value ? String(value) : "Sin código"
                  }
                />

                <InlineEditableField
                  label="Costo Estimado"
                  value={purchase_request.estimated_cost}
                  field="estimated_cost"
                  type="number"
                  updateUrl={purchaseRequestHref}
                  icon={DollarSign}
                  formatValue={(value) =>
                    value
                      ? formatCurrency(Number(value), "ARS")
                      : "No especificado"
                  }
                  placeholder="0.00"
                />

                <InlineEditableField
                  label="Prioritario"
                  value={purchase_request.priority}
                  field="priority"
                  type="boolean"
                  updateUrl={purchaseRequestHref}
                  icon={Flag}
                />

                {/* Estado actual + historial */}
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span>Estado actual:</span>
                  <button
                    type="button"
                    onClick={() => setHistoryDialogOpen(true)}
                    className="inline-flex items-center"
                    title="Ver historial de estado"
                  >
                    <StatusBadge status={purchase_request.status} />
                  </button>
                  {status_history && status_history.length > 0 && (
                    <span className="opacity-80">
                      Último cambio:{" "}
                      {new Date(
                        status_history[status_history.length - 1].at,
                      ).toLocaleString()}
                    </span>
                  )}
                  <span className="opacity-60">·</span>
                  <button
                    type="button"
                    onClick={() => setHistoryDialogOpen(true)}
                    className="underline text-primary hover:opacity-80"
                  >
                    Ver historial de cambios
                  </button>
                </div>
                <StatusHistoryDialog
                  open={historyDialogOpen}
                  onOpenChange={setHistoryDialogOpen}
                  currentStatus={purchase_request.status}
                  history={status_history}
                />
              </div>
            </div>

            <Separator className="my-6" />

            <div className="rounded-lg border bg-background p-3">
              <InlineEditableField
                label="Observaciones Generales"
                value={purchase_request.general_observations}
                field="general_observations"
                type="textarea"
                updateUrl={purchaseRequestHref}
                icon={FileText}
                placeholder="Agregar observaciones..."
                maxLength={500}
              />
            </div>
          </CardContent>
        </Card>

        {/* Comentarios */}
        <CommentsThread
          comments={comments}
          commentsPath={commentsHref}
          resourceLabel="la solicitud"
        />

        {/* Request Items */}
        <Card className="h-full min-h-[400px]">
          <CardHeader>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <CardTitle>Ítems de la Solicitud de compra</CardTitle>
                <CardDescription>
                  Haz clic en cualquier campo para editarlo. Enter guarda, Esc
                  cancela.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <ButtonGroup>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    title="Exportar ítems como PDF"
                  >
                    <a
                      href={`${purchaseRequestHref}/export_request_items.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="h-4 w-4" />
                      Exportar PDF
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddItemDialogOpen(true)}
                    className="flex items-center gap-2"
                    title="Agregar un producto a la solicitud"
                  >
                    <Plus className="h-4 w-4" />
                    Agregar producto
                  </Button>
                  <HelpButton
                    onClick={requestItemsHelp.open}
                    label="Ayuda"
                    variant="outline"
                  />
                </ButtonGroup>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Toolbar chips */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ButtonGroup className="pointer-events-none [&>*]:cursor-default [&>*]:hover:bg-transparent">
                <ButtonGroupText className="gap-1">
                  <Package className="h-3.5 w-3.5" /> {totalItems} ítems
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <CheckCircle className="h-3.5 w-3.5" /> {assignedCount} con
                  producto
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText
                  className={cn(
                    "gap-1",
                    unassignedCount > 0 &&
                      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
                  )}
                >
                  <AlertTriangle className="h-3.5 w-3.5" /> {unassignedCount}{" "}
                  sin producto
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <DollarSign className="h-3.5 w-3.5" /> {pricedCount} cotizados
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <DollarSign className="h-3.5 w-3.5" />{" "}
                  {formatCurrency(estimatedTotalARS, "ARS")}
                </ButtonGroupText>
              </ButtonGroup>
            </div>

            <div className="mb-4">
              <CurrencyFormatHint />
            </div>

            {request_items.length > 0 ? (
              <div className="min-h-[300px] rounded-lg border bg-background p-2">
                {hasUnmatchedItems && (
                  <div className="mb-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Hay ítems sin producto asignado. Asigna los productos para
                    ver la comparativa. (Haz doble clic en la celda para editar)
                  </div>
                )}
                <PurchaseRequestItemsTable
                  items={request_items}
                  lastPurchasesByRequestItemId={
                    last_purchases_by_request_item_id
                  }
                  unitOptions={unitOptions}
                  onOpenProductPicker={openProductPicker}
                  onOpenHistory={openHistory}
                  onDeleteItem={handleDeleteItem}
                  onUpdateItem={handleUpdateItem}
                  hasRawDescriptions={hasRawDescriptions}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="mb-2">No hay ítems en esta Solicitud de compra</p>
                <p className="text-sm">
                  Los ítems se crean automáticamente al procesar un documento de
                  solicitud
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal: Seleccionar producto (edición de ítem) */}
        <Dialog
          open={productPickerOpen}
          onOpenChange={(open) => {
            if (!open) {
              setProductPickerItem(null);
              setProductPickerOption(null);
            }
            setProductPickerOpen(open);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Asignar producto</DialogTitle>
              <DialogDescription>
                Busca y selecciona un producto del catálogo para el ítem.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Producto</Label>
                <AsyncCombobox
                  value={productPickerOption}
                  onChange={(opt) => {
                    setProductPickerOption(opt);
                  }}
                  defaultOptions={true}
                  cacheOptions={true}
                  loadOptions={async (inputValue: string) => {
                    const params = new URLSearchParams();
                    params.set("term", inputValue || "");
                    const res = await fetch(
                      `${routes.products.search()}?${params.toString()}`,
                    );
                    if (!res.ok) return [];
                    const data = (await res.json()) as {
                      value: number | string;
                      label: string;
                    }[];
                    return data.map((d) => ({
                      value: d.value,
                      label: d.label,
                    }));
                  }}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProductPickerOpen(false);
                    setProductPickerItem(null);
                    setProductPickerOption(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    if (!productPickerItem) return;
                    const productId = productPickerOption?.value
                      ? Number(productPickerOption.value)
                      : null;
                    router.patch(
                      requestItemHref(productPickerItem),
                      {
                        request_item: { product_id: productId },
                      },
                      {
                        preserveScroll: true,
                        onSuccess: () => {
                          setProductPickerOpen(false);
                          setProductPickerItem(null);
                          setProductPickerOption(null);
                        },
                      },
                    );
                  }}
                  disabled={!productPickerItem}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal: Agregar producto a la solicitud */}
        <Dialog
          open={addItemDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              resetNewItem();
              setQtyStr("");
            }
            setAddItemDialogOpen(open);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Agregar producto</DialogTitle>
              <DialogDescription>
                Selecciona un producto de la base, cantidad y unidad.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Producto (async combobox) */}
              <div className="space-y-2">
                <Label>Producto *</Label>
                <AsyncCombobox
                  value={selectedProduct}
                  onChange={(opt) => {
                    setSelectedProduct(opt);
                    setNewItemData("request_item", {
                      ...newItemData.request_item,
                      product_id: opt?.value ? Number(opt.value) : null,
                    });
                  }}
                  defaultOptions={true}
                  cacheOptions={true}
                  loadOptions={async (inputValue: string) => {
                    const params = new URLSearchParams();
                    params.set("term", inputValue || "");
                    const res = await fetch(
                      `${routes.products.search()}?${params.toString()}`,
                    );
                    if (!res.ok) return [];
                    // Expect: [{ id, value, label, internal_code }]
                    const data = (await res.json()) as {
                      value: number | string;
                      label: string;
                    }[];
                    return data.map((d) => ({
                      value: d.value,
                      label: d.label,
                    }));
                  }}
                />
              </div>

              {/* Cantidad y Unidad */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cantidad *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 10 o 10,5"
                    value={qtyStr}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                        setQtyStr(raw);
                        const parsed = parseCommaNumber(raw);
                        setNewItemData("request_item", {
                          ...newItemData.request_item,
                          quantity: parsed ?? newItemData.request_item.quantity,
                        });
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <Select
                    onValueChange={(val) =>
                      setNewItemData("request_item", {
                        ...newItemData.request_item,
                        unit: val,
                      })
                    }
                    value={newItemData.request_item.unit}
                  >
                    <SelectTrigger className="w-full h-10">
                      <SelectValue placeholder="Selecciona unidad" />
                    </SelectTrigger>
                    <SelectContent className="w-[--radix-select-trigger-width]">
                      {unitOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAddItemDialogOpen(false);
                    resetNewItem();
                    setQtyStr("");
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    postNewItem(requestItemsHref, {
                      preserveScroll: true,
                      onSuccess: () => {
                        resetNewItem();
                        setQtyStr("");
                        setAddItemDialogOpen(false);
                      },
                    });
                  }}
                  disabled={
                    creatingItem ||
                    !newItemData.request_item.product_id ||
                    !newItemData.request_item.unit ||
                    qtyStr.trim() === "" ||
                    parseCommaNumber(qtyStr) === null
                  }
                >
                  {creatingItem ? "Guardando…" : "Agregar Ítem"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Gestión de Invitaciones — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
            <InvitationsManager
              purchaseRequestId={purchase_request.id}
              purchaseRequestNumber={String(purchase_request.id)}
              existingInvitations={quote_invitations}
              disabled={purchase_request.status === "approved"}
            />
          </GroupGate>
        )}

        {/* Envío de Solicitudes y Carga de Documentos — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Gestión de Cotizaciones
                    </CardTitle>
                    <CardDescription>
                      Elige cómo cargar los presupuestos: enviar solicitudes,
                      crear manualmente, o subir documentos
                    </CardDescription>
                  </div>
                  <HelpButton
                    onClick={quoteManagementHelp.open}
                    label="Ayuda"
                    variant="outline"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {purchase_request.status === "approved" && (
                  <p className="text-sm text-muted-foreground mb-4">
                    La solicitud fue aprobada. No se pueden cargar más
                    cotizaciones.
                  </p>
                )}
                <ButtonGroup>
                  {purchase_request.status === "approved" ? (
                    <Button disabled>
                      <Mail className="h-4 w-4 mr-2" />
                      Enviar Solicitud
                    </Button>
                  ) : (
                    <Button asChild>
                      <Link href={newQuoteRequestHref}>
                        <Mail className="h-4 w-4 mr-2" />
                        Enviar Solicitud
                      </Link>
                    </Button>
                  )}
                  <ButtonGroupSeparator />
                  {purchase_request.status === "approved" ? (
                    <Button variant="outline" disabled>
                      <Edit className="h-4 w-4 mr-2" />
                      Manual
                    </Button>
                  ) : (
                    <Button asChild variant="outline">
                      <Link href={newQuoteHref}>
                        <Edit className="h-4 w-4 mr-2" />
                        Manual
                      </Link>
                    </Button>
                  )}
                  <ButtonGroupSeparator />
                  <Dialog
                    open={uploadDialogOpen}
                    onOpenChange={setUploadDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={purchase_request.status === "approved"}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Cargar
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>
                          Subir documentos de cotizaciones
                        </DialogTitle>
                        <DialogDescription>
                          Arrastra documentos (PDF o imágenes) aquí o haz clic
                          para seleccionar múltiples archivos de cotizaciones
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="w-full">
                          <Dropzone
                            onDrop={handleFileDrop}
                            accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                            disabled={processing}
                            containerClassName="w-full"
                            dropZoneClassName="group relative w-full h-40 rounded-xl border border-solid border-border outline-none bg-gradient-to-br from-muted/40 to-muted/10 hover:from-accent/50 hover:to-accent/20 overflow-hidden"
                          >
                            {(dz) => (
                              <div className="relative flex h-full w-full items-center justify-center">
                                <div className="pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(600px_circle_at_center,black,transparent)] bg-[linear-gradient(to_right,rgba(120,119,198,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(120,119,198,0.08)_1px,transparent_1px)] bg-[size:14px_14px]" />
                                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(400px_circle_at_center,rgba(99,102,241,0.14),transparent_60%)]" />
                                <div className="relative flex flex-col items-center text-center gap-2 px-6">
                                  <div
                                    className={`h-10 w-10 rounded-full ring-1 flex items-center justify-center transition-all ${
                                      dz.isDragAccept
                                        ? "bg-indigo-500/10 ring-indigo-400/50"
                                        : dz.isDragReject
                                          ? "bg-destructive/10 ring-destructive/40"
                                          : "bg-background/60 ring-border"
                                    }`}
                                  >
                                    <Upload
                                      className={`h-5 w-5 transition-colors ${
                                        dz.isDragAccept
                                          ? "text-indigo-500"
                                          : dz.isDragReject
                                            ? "text-destructive"
                                            : "text-muted-foreground"
                                      }`}
                                    />
                                  </div>
                                  <div className="text-sm font-medium">
                                    {dz.isDragActive
                                      ? "Suelta los documentos para cargar"
                                      : data.pdfs.length > 0
                                        ? "Agregar más documentos"
                                        : "Arrastra documentos aquí o haz clic para cargar"}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground/80">
                                    PDF o imágenes (JPG, PNG)
                                  </p>
                                </div>
                              </div>
                            )}
                          </Dropzone>
                        </div>

                        {data.pdfs.length > 0 && (
                          <div className="space-y-2">
                            <p className="font-medium">
                              Archivos seleccionados ({data.pdfs.length}):
                            </p>
                            <div className="max-h-48 overflow-y-auto space-y-2">
                              {data.pdfs.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex justify-between items-center flex-row w-full h-12 px-3 border border-gray-200 rounded-lg"
                                >
                                  <div className="flex items-center flex-row gap-3 h-full">
                                    <FileText className="text-red-600 w-5 h-5" />
                                    <div className="flex flex-col gap-0">
                                      <div className="text-sm font-medium leading-snug">
                                        {file.name}
                                      </div>
                                      <div className="text-xs text-gray-500 leading-tight">
                                        Documento • {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFileRemove(index)}
                                    disabled={processing}
                                    className="p-1 h-8 w-8 rounded-full hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => setUploadDialogOpen(false)}
                            disabled={processing}
                          >
                            Cancelar
                          </Button>
                          <Button
                            disabled={processing || data.pdfs.length === 0}
                            onClick={handleUploadSubmit}
                          >
                            {processing && (
                              <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                            )}
                            {processing
                              ? "Procesando..."
                              : `Subir y procesar ${data.pdfs.length} documento${data.pdfs.length !== 1 ? "s" : ""}`}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </ButtonGroup>
              </CardContent>
            </Card>
          </GroupGate>
        )}

        {/* Cotizaciones generadas por IA — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
            {(quotes.length > 0 || quote_documents.length > 0) && (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Cotizaciones
                      </CardTitle>
                      <CardDescription>
                        Resultados procesados automáticamente desde los
                        documentos
                      </CardDescription>
                    </div>
                    <HelpButton
                      onClick={aiQuotesHelp.open}
                      label="Ayuda"
                      variant="outline"
                    />
                  </div>
                  {/* Header metrics (compact) */}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="secondary" className="gap-1">
                          <FileText className="h-3.5 w-3.5" /> Docs:{" "}
                          {quote_documents.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Documentos subidos</TooltipContent>
                    </Tooltip>
                    {docCounts.failed > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3.5 w-3.5" /> Errores:{" "}
                            {docCounts.failed}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          Archivos con error de procesamiento
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="gap-1">
                          <Mail className="h-3.5 w-3.5" /> Cotizaciones:{" "}
                          {quotes.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        Total de cotizaciones generadas
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Documentos cargados - sección compacta */}
                    {quote_documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm font-medium text-muted-foreground">
                          Documentos:
                        </span>
                        {quote_documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center gap-2 px-2 py-1 bg-background rounded border text-xs"
                          >
                            <FileText className="h-3 w-3" />
                            <span className="max-w-24 truncate">
                              {doc.pdf?.filename ?? "Sin nombre"}
                            </span>
                            {(() => {
                              const status =
                                doc.status as keyof typeof docStatusConfig;
                              const color = docStatusConfig[status]?.color;
                              const label = docStatusConfig[status]?.label;
                              const Icon =
                                status === "pending"
                                  ? (props: ComponentProps<typeof Clock>) => (
                                      <Clock {...props} />
                                    )
                                  : status === "processing"
                                    ? (props: ComponentProps<typeof Clock>) => (
                                        <LoaderCircle
                                          {...props}
                                          className={
                                            (props.className ?? "") +
                                            " animate-spin"
                                          }
                                        />
                                      )
                                    : status === "processed"
                                      ? (
                                          props: ComponentProps<typeof Clock>,
                                        ) => <CheckCircle2 {...props} />
                                      : (
                                          props: ComponentProps<typeof Clock>,
                                        ) => <TriangleAlert {...props} />;
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      className={`text-xs px-1 py-0 h-5 gap-1 cursor-help ${color}`}
                                    >
                                      <Icon className="h-3 w-3" /> {label}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Estado del documento
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })()}
                            {doc.pdf?.url && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0"
                                    asChild
                                  >
                                    <a
                                      href={doc.pdf.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Ver documento</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-4 w-4 p-0 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Eliminar documento
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        ))}
                        {/* Ayuda discreta bajo la lista de documentos */}
                        <div className="w-full text-[11px] text-muted-foreground mt-1">
                          ¿La IA procesó mal el documento o hubo un error?
                          Elimina el archivo y súbelo nuevamente. No olvides
                          descargarlo antes de eliminarlo; de lo contrario, no
                          podrás volver a subirlo.
                        </div>
                      </div>
                    )}

                    {/* Cotizaciones - tabla compacta */}
                    {supplierQuoteGroups.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="h-8 text-xs">
                              Proveedor
                            </TableHead>
                            <TableHead className="h-8 text-xs w-[80px] text-center">
                              Ítems
                            </TableHead>
                            <TableHead className="h-8 text-xs w-[90px]">
                              Fecha
                            </TableHead>
                            <TableHead className="h-8 text-xs text-right w-[130px]">
                              Total
                            </TableHead>
                            <TableHead className="h-8 text-xs w-[80px]" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {supplierQuoteGroups.map(({ latest: quote, history }) => {
                            const quotedCount = quote.quote_items_count ?? 0;
                            const requestedCount =
                              quote.requested_items_count ?? totalItems;
                            const isComplete = quotedCount >= requestedCount;

                            return (
                              <TableRow key={quote.id} className="h-10">
                                <TableCell className="py-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm truncate max-w-[180px]">
                                      {quote.supplier.name}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] h-5 px-1.5"
                                    >
                                      {quote.currency}
                                    </Badge>
                                    {quote.context_notes && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Info className="h-3.5 w-3.5 text-blue-500" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          {quote.context_notes}
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="py-1.5 text-center">
                                  <span
                                    className={cn(
                                      "text-xs font-medium",
                                      isComplete
                                        ? "text-green-600"
                                        : "text-amber-600",
                                    )}
                                  >
                                    {quotedCount}/{requestedCount}
                                  </span>
                                </TableCell>
                                <TableCell className="py-1.5 text-xs text-muted-foreground">
                                  {quote.quote_date}
                                </TableCell>
                                <TableCell className="py-1.5 text-right">
                                  <div className="font-medium text-sm">
                                    {formatCurrency(
                                      quote.total_amount,
                                      quote.currency,
                                    )}
                                  </div>
                                  {quote.currency === "USD" &&
                                    quote.total_in_ars && (
                                      <div className="text-[10px] text-muted-foreground">
                                        {formatCurrency(
                                          quote.total_in_ars,
                                          "ARS",
                                        )}
                                      </div>
                                    )}
                                </TableCell>
                                <TableCell className="py-1.5">
                                  <div className="flex items-center justify-end gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7"
                                          asChild
                                        >
                                          <Link
                                            href={purchaseRequestQuoteHref(
                                              quote.id,
                                            )}
                                          >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                          </Link>
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Ver cotización
                                      </TooltipContent>
                                    </Tooltip>
                                    {quote.pdf?.url && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span>
                                            <PdfViewerSheet
                                              pdfUrl={quote.pdf.url}
                                              title={`${quote.supplier.name} - ${quote.quote_date}`}
                                            >
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                              >
                                                <FileText className="h-3.5 w-3.5" />
                                              </Button>
                                            </PdfViewerSheet>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          Ver documento
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {history.length > 0 && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span>
                                            <QuoteHistoryDialog
                                              supplierName={quote.supplier.name}
                                              purchaseRequestId={purchase_request.id}
                                              versions={history}
                                              trigger={
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-7 w-7"
                                                  title={`Historial (${history.length})`}
                                                >
                                                  <ClipboardList className="h-3.5 w-3.5" />
                                                  <span className="sr-only">
                                                    Historial ({history.length})
                                                  </span>
                                                </Button>
                                              }
                                            />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          Historial de cotizaciones
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      quote_documents.length > 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">
                            Los documentos están siendo procesados...
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </GroupGate>
        )}

        {/* Supplier Assignment Alert */}
        {hasDefaultSupplierQuotes && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Configuración de Proveedores Requerida
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Debes asignar un proveedor a todas las cotizaciones antes de
                    poder ver la comparativa de precios
                  </p>
                  <div className="mt-3 space-y-1">
                    {quotesWithDefaultSupplier.map((quote) => (
                      <Link
                        key={quote.id}
                        href={purchaseRequestQuoteHref(quote.id)}
                        className="text-xs text-amber-700 hover:text-amber-900 underline block"
                      >
                        • Cotización #{quote.quote_number ?? quote.id} - Asignar proveedor
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparativa y Selección — supervisor, buyer y participantes del flujo de aprobación */}
        {(() => {
          const userCanSeeComponent = canSeeApprovalComponent(
            userGroups,
            approval,
            shared.auth?.user?.id ?? 0,
            CAN_MANAGE_QUOTES,
          );

          if (!userCanSeeComponent) return null;

          return price_comparison && !hasDefaultSupplierQuotes ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <CardTitle>Comparativa y selección</CardTitle>
                    <CardDescription>
                      Revisa la comparativa y selecciona los productos a comprar
                      por proveedor.
                    </CardDescription>
                  </div>
                  <HelpButton
                    onClick={comparisonHelp.open}
                    label="Ayuda"
                    variant="outline"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const hasSelections = Boolean(
                    approval?.has_purchase_selections ||
                      (price_comparison?.rows?.some((row) =>
                        row.prices?.some((p) => p?.is_selected),
                      ) ??
                        false),
                  );
                  const defaultTab = hasSelections
                    ? "selection"
                    : "comparative";
                  return (
                    <Tabs defaultValue={defaultTab}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <TabsList className="flex w-full flex-col gap-2 rounded-2xl bg-muted/60 p-1 sm:w-auto sm:flex-row sm:gap-0">
                          <TabsTrigger
                            className="flex-1 sm:flex-none"
                            value="comparative"
                          >
                            Comparativa de precios
                          </TabsTrigger>
                          <TabsTrigger
                            className="flex-1 sm:flex-none"
                            value="selection"
                          >
                            Selección de compra
                          </TabsTrigger>
                        </TabsList>
                      </div>
                      <TabsContent value="comparative" className="mt-4">
                        <PriceComparisonTable
                          comparison={price_comparison}
                          purchaseRequestId={purchase_request.id}
                          lastPurchasesByRequestItemId={
                            last_purchases_by_request_item_id
                          }
                          onOpenHistory={(id, desc) => openHistory(id, desc)}
                        />
                      </TabsContent>
                      <TabsContent value="selection" className="mt-4">
                        <div className="space-y-7">
                          <PurchaseSelectionTable
                            comparison={price_comparison}
                            purchaseRequestId={purchase_request.id}
                            canExport={canExportPurchaseOrders(approval.status)}
                            canSaveSelections={
                              purchase_request.status !== "approved"
                            }
                            purchaseRequestInfo={{
                              id: purchase_request.id,
                              request_number: String(purchase_request.id),
                              sector: {
                                id: purchase_request.sector?.id,
                                name: purchase_request.sector?.name,
                                code: purchase_request.sector?.code ?? null,
                              },
                            }}
                            onSaved={() =>
                              router.reload({ only: ["price_comparison"] })
                            }
                          />
                          <SelectedItemsTable
                            comparison={price_comparison}
                            selectionsTotals={approval?.selections_totals}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  );
                })()}
              </CardContent>
            </Card>
          ) : null;
        })()}

        <PurchaseRequestApprovalPanel
          approval={approval}
          purchaseRequestId={purchase_request.id}
          sectorId={purchase_request.sector?.id}
          sectorName={purchase_request.sector?.name}
        />

        {/* Facturas Relacionadas */}
        <InvoicesTable
          invoices={invoices}
          purchaseRequestId={purchase_request.id}
        />

        {/* Remitos Relacionados */}
        <DeliveryNotesTable
          deliveryNotes={delivery_notes}
          purchaseRequestId={purchase_request.id}
        />

        {/* Acciones destructivas al final */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Acciones</CardTitle>
              <CardDescription>
                Operaciones avanzadas sobre esta solicitud
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (
                      window.confirm(
                        "¿Estás seguro de que quieres eliminar esta solicitud de compra?\n\n" +
                          "Esta acción eliminará PERMANENTEMENTE:\n" +
                          "• La solicitud de compra\n" +
                          "• Todos los items de la solicitud\n" +
                          "• Todas las cotizaciones recibidas\n" +
                          "• Todos los documentos\n" +
                          "• Todas las invitaciones enviadas\n\n" +
                          "Esta acción NO se puede deshacer.",
                      )
                    ) {
                      router.delete(purchaseRequestHref, {
                        onSuccess: () => {
                          router.get(purchaseRequestsHref);
                        },
                      });
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Solicitud de compra
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Historial de compras (right sheet) */}
        <PurchaseHistorySheet
          open={historySheetOpen}
          onOpenChange={setHistorySheetOpen}
          requestItemId={historyItemId ?? undefined}
          requestItemDescription={historyItemDescription ?? undefined}
        />

        {/* Help Dialogs */}
        <HelpDialog
          content={overviewHelp.content}
          open={overviewHelp.isOpen}
          onOpenChange={overviewHelp.setIsOpen}
        />
        <HelpDialog
          content={generalInfoHelp.content}
          open={generalInfoHelp.isOpen}
          onOpenChange={generalInfoHelp.setIsOpen}
        />
        <HelpDialog
          content={requestItemsHelp.content}
          open={requestItemsHelp.isOpen}
          onOpenChange={requestItemsHelp.setIsOpen}
        />
        <HelpDialog
          content={aiQuotesHelp.content}
          open={aiQuotesHelp.isOpen}
          onOpenChange={aiQuotesHelp.setIsOpen}
        />
        <HelpDialog
          content={quoteManagementHelp.content}
          open={quoteManagementHelp.isOpen}
          onOpenChange={quoteManagementHelp.setIsOpen}
        />
        <HelpDialog
          content={comparisonHelp.content}
          open={comparisonHelp.isOpen}
          onOpenChange={comparisonHelp.setIsOpen}
        />
      </div>
    </AppLayout>
  );
}
